import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import {
  Area,
  AreaChart,
  CartesianGrid,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { Layout } from "@/components/site/Layout";
import { Button } from "@/components/ui/button";
import { getGoldCot, type CotDTO, type CotWeek } from "@/lib/cot.functions";
import { CotPositionBreakdown } from "@/components/site/CotPositionBreakdown";
import { CotPlainSummary } from "@/components/site/CotPlainSummary";
import { positionReading } from "@/lib/cot-positioning";
import { isFreshReport, nextRelease } from "@/lib/cot-schedule";

const SITE = "https://xauproacademy.com";
const PATH = "/gold-cot-report";


const FAQ = [
  {
    q: "What is the Commitment of Traders report for gold?",
    a: "A weekly CFTC publication listing open futures positions in COMEX gold by trader category. Positions are measured as of Tuesday and released on Friday at 15:30 New York time.",
  },
  {
    q: "Who are Managed Money traders?",
    a: "Hedge funds and commodity trading advisors. Their net position is the most watched speculative gauge in gold because it shifts with trend and macro conviction.",
  },
  {
    q: "Why are Commercials usually net short gold?",
    a: "Producers, merchants and swap dealers hedge physical inventory and client exposure. Their net short typically grows as price rises, so it mirrors speculative positioning rather than predicting direction.",
  },
  {
    q: "What does the 3 year COT Index measure?",
    a: "Where the current Managed Money net position sits between its lowest (0) and highest (100) level of the last 156 weeks. Readings near the extremes describe crowded positioning, not timing.",
  },
  {
    q: "Can COT data be used as an entry signal?",
    a: "No. The data is three days old at release and has no entry, invalidation or risk definition. It is context for bias. Execution still requires structure, liquidity and displacement on intraday timeframes.",
  },
];

export const Route = createFileRoute("/gold-cot-report")({
  head: () => ({
    meta: [
      { title: "Gold COT Report: Commitment of Traders Chart | XAU Pro Academy" },
      {
        name: "description",
        content:
          "Free gold COT report with hedge fund, swap dealer and producer long/short contracts, weekly position changes, clear explanations and a 3 year COT Index.",
      },
      {
        name: "keywords",
        content:
          "gold COT report, XAUUSD commitment of traders, gold COT data, managed money gold positioning, COT index gold, CFTC gold report",
      },
      { property: "og:title", content: "Gold COT Report: Institutional Positioning in XAUUSD" },
      {
        property: "og:description",
        content: "See who increased longs, reduced exposure or covered shorts in COMEX gold. Official CFTC data with weekly changes and historical charts.",
      },
      { property: "og:type", content: "website" },
      { property: "og:url", content: `${SITE}${PATH}` },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: `${SITE}${PATH}` }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Dataset",
          name: "Gold Commitment of Traders Positioning (COMEX)",
          description: "Weekly Managed Money, Commercial and Non-Reportable net positions in COMEX gold futures.",
          url: `${SITE}${PATH}`,
          license: "https://xauproacademy.com/terms", isAccessibleForFree: true, inLanguage: "en", variableMeasured: ["Managed Money net position", "Commercial net position", "Non-Reportable net position", "Open interest", "COT Index (3 year)"], temporalCoverage: "2021-01-01/..", distribution: [{ "@type": "DataDownload", encodingFormat: "text/html", contentUrl: `${SITE}${PATH}` }], dateModified: new Date().toISOString().slice(0, 10), creator: { "@type": "Organization", name: "XAU Pro Academy", url: SITE },
          isBasedOn: "https://www.cftc.gov/MarketReports/CommitmentsofTraders/index.htm",
                  }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: FAQ.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: SITE },
            { "@type": "ListItem", position: 2, name: "Gold COT Report", item: `${SITE}${PATH}` },
          ],
        }),
      },
    ],
  }),
  loader: () => getGoldCot(),
  staleTime: 60 * 60 * 1000,
  component: CotPage,
});

const fmt = (x: number) => x.toLocaleString("en-US");
const signed = (x: number) => (x > 0 ? `+${fmt(x)}` : fmt(x));
const fmtDate = (d: string) =>
  new Date(`${d}T00:00:00Z`).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric", timeZone: "UTC" });

function regime(idx: number | null) {
  if (idx == null) return { label: "Unavailable", tone: "text-muted-foreground" };
  if (idx >= 90) return { label: "Extreme long", tone: "text-destructive" };
  if (idx >= 70) return { label: "Crowded long", tone: "text-gold" };
  if (idx > 30) return { label: "Neutral range", tone: "text-foreground" };
  if (idx > 10) return { label: "Light positioning", tone: "text-gold" };
  return { label: "Near range low", tone: "text-gold" };
}

function readout(idx: number | null, latest: CotWeek, prev: CotWeek | undefined) {
  const reading = positionReading(latest.positions.managedMoney, prev?.positions.managedMoney);
  const lines = [
    `Managed Money: ${reading.detail} Current net ${latest.mmNet >= 0 ? "long" : "short"}: ${fmt(Math.abs(latest.mmNet))} contracts.`,
  ];
  if (idx != null) {
    if (idx >= 80)
      lines.push(
        "Managed Money net exposure is near the top of its 3 year range. This describes relatively elevated positioning, not a reversal forecast or evidence of a future liquidity sweep.",
      );
    else if (idx <= 20)
      lines.push(
        "Managed Money net exposure is near the bottom of its 3 year range. A low index does not necessarily mean net short, and does not establish that gold is about to rise.",
      );
    else
      lines.push(
        "Positioning is inside the middle of its historical range. The index alone does not establish a directional edge; execution still requires H4/H1 structure and confirmation.",
      );
  }
  lines.push("COT is context for bias, never an entry trigger. It carries no invalidation and is three days old on release.");
  return lines;
}

function CotPage() {
  const data = Route.useLoaderData();
  const [range, setRange] = useState<52 | 156 | 260>(156);
  const weeks = data.weeks;
  const latest = weeks[weeks.length - 1];
  const prev = weeks[weeks.length - 2];
  const series = useMemo(() => weeks.slice(-range), [weeks, range]);

  const indexSeries = useMemo(() => {
    return weeks.slice(-range).map((w) => {
      const i = weeks.indexOf(w);
      const win = weeks.slice(Math.max(0, i - 155), i + 1).map((x) => x.mmNet);
      const mn = Math.min(...win);
      const mx = Math.max(...win);
      return { date: w.date, idx: mx > mn ? ((w.mmNet - mn) / (mx - mn)) * 100 : 50 };
    });
  }, [weeks, range]);

  const [selected, setSelected] = useState<string>(latest?.date ?? "");
  const foundIndex = weeks.findIndex((w) => w.date === selected);
  const selIndex = foundIndex >= 0 ? foundIndex : weeks.length - 1;
  const cur = weeks[selIndex] ?? latest;
  const prv = weeks[selIndex - 1];
  const isHistorical = !!latest && cur?.date !== latest.date;
  const curIdx = useMemo(() => {
    if (!cur) return null;
    if (!isHistorical) return data.mmIndex3y;
    const win = weeks.slice(Math.max(0, selIndex - 155), selIndex + 1).map((x) => x.mmNet);
    const mn = Math.min(...win), mx = Math.max(...win);
    return mx > mn ? ((cur.mmNet - mn) / (mx - mn)) * 100 : 50;
  }, [weeks, selIndex, cur, isHistorical, data.mmIndex3y]);
  const rg = regime(curIdx);
  const [vars, setVars] = useState<Record<string, string>>({});
  useEffect(() => {
    const cs = getComputedStyle(document.documentElement);
    const out: Record<string, string> = {};
    for (const k of ["gold", "chart-silver", "chart-silver-deep", "border", "muted-foreground", "destructive", "card"]) out[k] = cs.getPropertyValue(`--${k}`).trim();
    setVars(out);
  }, []);
  const c = (k: string) => vars[k] || "currentColor";

  return (
    <Layout>
      <section className="relative overflow-hidden border-b border-border/60 bg-gradient-hero">
        <div className="absolute inset-0 grid-bg opacity-30" />
        <div className="container relative mx-auto max-w-6xl px-4 pb-14 pt-16 md:pt-24">
          <span className="inline-flex items-center rounded-full border border-gold/30 bg-gold-muted px-3 py-1 font-mono text-[10px] uppercase tracking-[0.24em] text-gold">
            Institutional Tools · CFTC Data
          </span>
          <h1 className="mt-6 max-w-3xl font-display text-4xl font-bold tracking-tight md:text-5xl">
            Gold COT Report: <span className="text-gradient-gold">institutional positioning</span> in XAUUSD
          </h1>
          <p className="mt-4 max-w-2xl text-muted-foreground">
            See how funds, swap dealers and producers changed their gold futures exposure. Long and short contracts,
            weekly changes and historical context, without confusing a hedge with a directional bet.
          </p>
          {latest && (
            <p className="mt-4 font-mono text-[11px] uppercase tracking-[0.18em] text-muted-foreground">
              Positions as of {fmtDate(latest.date)} · Released Fridays 15:30 ET · Source: CFTC
            </p>
          )}
          {latest && <ReleaseStatus asOf={latest.date} />}
        </div>
      </section>

      {!latest ? (
        <section className="container mx-auto max-w-6xl px-4 py-16 text-center text-muted-foreground">
          COT data is temporarily unavailable. Please check back shortly.
        </section>
      ) : (
        <>
          {data.stale && <p role="status" className="container mx-auto max-w-6xl px-4 pt-6 text-sm text-gold">The source is temporarily unavailable. Showing the last successfully retrieved report, as of {fmtDate(latest.date)}.</p>}
          <section className="sticky top-16 z-20 border-b border-border/60 bg-background/90 backdrop-blur">
            <div className="container mx-auto flex max-w-6xl flex-wrap items-center justify-between gap-3 px-4 py-3">
              <label className="flex items-center gap-3 text-xs text-muted-foreground">
                <span className="font-mono uppercase tracking-[0.18em]">Report date</span>
                <select aria-label="Report date" value={cur.date} onChange={(e) => setSelected(e.target.value)} className="rounded-md border border-border bg-card px-3 py-2 font-mono text-sm text-foreground">
                  {weeks.slice(-12).reverse().map((w) => <option key={w.date} value={w.date}>{w.date}{w.date === latest.date ? " (latest)" : ""}</option>)}
                </select>
              </label>
              {isHistorical ? (
                <div className="flex items-center gap-3 text-xs">
                  <span className="text-gold">Viewing historical snapshot of {fmtDate(cur.date)}</span>
                  <Button size="sm" variant="outline" onClick={() => setSelected(latest.date)}>Latest report</Button>
                </div>
              ) : (
                <span className="font-mono text-xs uppercase text-muted-foreground">Latest report · Tuesday snapshot</span>
              )}
            </div>
          </section>
          <CotPlainSummary latest={cur} prev={prv} />
          <CotPositionBreakdown weeks={weeks} selected={cur.date} />
          <section className="container mx-auto max-w-6xl px-4 py-10">
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <Stat label="Managed Money net" value={signed(cur.mmNet)} sub={`${signed(cur.mmNet - (prv?.mmNet ?? cur.mmNet))} w/w`} />
              <Stat
                label="COT Index (3Y)"
                value={curIdx != null ? curIdx.toFixed(0) : "n/a"}
                sub={rg.label}
                tone={rg.tone}
              />
              <Stat label="Commercials net" value={signed(cur.commNet)} sub={`${signed(cur.commNet - (prv?.commNet ?? cur.commNet))} w/w`} />
              <Stat label="Open interest" value={fmt(cur.openInterest)} sub={`${signed(cur.openInterest - (prv?.openInterest ?? cur.openInterest))} w/w`} />
            </div>

            <div className="mt-6 border-y border-gold/30 py-6">
              <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-gold">Desk reading</div>
              <ul className="mt-3 space-y-2 text-sm text-muted-foreground">
                {readout(curIdx, cur, prv).map((l) => (
                  <li key={l} className="flex gap-2">
                    <span className="mt-2 h-1 w-1 shrink-0 rounded-full bg-gold" />
                    {l}
                  </li>
                ))}
              </ul>
            </div>
          </section>

          <section className="container mx-auto max-w-6xl px-4 pb-10">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <h2 className="font-display text-2xl font-bold">Net positioning by trader class</h2>
              <div className="flex gap-1 rounded-lg border border-border/60 p-1">
                {([52, 156, 260] as const).map((r) => (
                  <Button
                    key={r}
                    variant={range === r ? "gold" : "ghost"}
                    size="sm"
                    aria-pressed={range === r}
                    onClick={() => setRange(r)}
                    className={`rounded-md px-3 py-1 font-mono text-[11px] uppercase tracking-wider ${range === r ? "bg-gold text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}
                  >
                    {r === 52 ? "1Y" : r === 156 ? "3Y" : "5Y"}
                  </Button>
                ))}
              </div>
            </div>
            <div className="mt-4 h-[360px] rounded-xl border border-border/60 bg-card/60 p-3">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={series} margin={{ top: 10, right: 12, left: 0, bottom: 0 }}>
                  <CartesianGrid stroke={c("border")} strokeDasharray="2 4" vertical={false} />
                  <XAxis dataKey="date" tick={{ fontSize: 10, fill: c("muted-foreground") }} tickFormatter={(d) => d.slice(0, 7)} minTickGap={40} />
                  <YAxis tick={{ fontSize: 10, fill: c("muted-foreground") }} tickFormatter={(v) => `${Math.round(v / 1000)}k`} width={44} />
                  <Tooltip
                    contentStyle={{ background: "var(--card)", border: "1px solid var(--border)", fontSize: 12 }} labelStyle={{ color: "var(--foreground)" }} itemStyle={{ color: "var(--foreground)" }}
                    labelFormatter={(d) => fmtDate(String(d))}
                    formatter={(v: number, name: string) => [fmt(v), name]}
                  />
                  <ReferenceLine y={0} stroke={c("muted-foreground")} />
                  <Line type="monotone" dataKey="mmNet" name="Managed Money" stroke={c("gold")} strokeWidth={2} dot={false} />
                  <Line type="monotone" dataKey="commNet" name="Commercials" stroke={c("chart-silver")} strokeWidth={1.5} dot={false} />
                  <Line type="monotone" dataKey="retailNet" name="Non-reportable" stroke={c("chart-silver-deep")} strokeWidth={1} strokeDasharray="3 3" dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </div>
            <div className="mt-2 flex flex-wrap gap-4 font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
              <span className="text-gold">━ Managed Money</span>
              <span>━ Commercials</span>
              <span>┅ Non-reportable</span>
            </div>
          </section>

          <section className="container mx-auto max-w-6xl px-4 pb-10">
            <h2 className="font-display text-2xl font-bold">Managed Money COT Index (rolling 3 year)</h2>
            <p className="mt-1 text-sm text-muted-foreground">0 = lowest net position in 156 weeks, 100 = highest. Bands mark crowded extremes.</p>
            <div className="mt-4 h-[260px] rounded-xl border border-border/60 bg-card/60 p-3">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={indexSeries} margin={{ top: 10, right: 12, left: 0, bottom: 0 }}>
                  <defs>
                    <linearGradient id="cotIdx" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor={c("gold")} stopOpacity={0.4} />
                      <stop offset="100%" stopColor={c("gold")} stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid stroke={c("border")} strokeDasharray="2 4" vertical={false} />
                  <XAxis dataKey="date" tick={{ fontSize: 10, fill: c("muted-foreground") }} tickFormatter={(d) => d.slice(0, 7)} minTickGap={40} />
                  <YAxis domain={[0, 100]} ticks={[0, 20, 50, 80, 100]} tick={{ fontSize: 10, fill: c("muted-foreground") }} width={32} />
                  <Tooltip
                    contentStyle={{ background: "var(--card)", border: "1px solid var(--border)", fontSize: 12 }} labelStyle={{ color: "var(--foreground)" }} itemStyle={{ color: "var(--foreground)" }}
                    labelFormatter={(d) => fmtDate(String(d))}
                    formatter={(v: number) => [v.toFixed(0), "COT Index"]}
                  />
                  <ReferenceLine y={80} stroke={c("destructive")} strokeDasharray="4 4" />
                  <ReferenceLine y={20} stroke={c("chart-silver")} strokeDasharray="4 4" />
                  <Area type="monotone" dataKey="idx" stroke={c("gold")} fill="url(#cotIdx)" strokeWidth={2} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </section>

          <section className="container mx-auto max-w-6xl px-4 pb-12">
            <h2 className="font-display text-2xl font-bold">Last 8 reports</h2>
            <div className="mt-4 overflow-x-auto rounded-xl border border-border/60">
              <table className="w-full min-w-[640px] text-sm">
                <thead className="bg-card/80 font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
                  <tr>
                    {["Date", "MM long", "MM short", "MM net", "Comm. net", "Non-report. net", "Open int."].map((h) => (
                      <th key={h} className="px-3 py-2 text-left">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="font-mono">
                  {weeks.slice(-8).reverse().map((w) => (
                    <tr key={w.date} className="border-t border-border/40">
                      <td className="px-3 py-2">{fmtDate(w.date)}</td>
                      <td className="px-3 py-2">{fmt(w.mmLong)}</td>
                      <td className="px-3 py-2">{fmt(w.mmShort)}</td>
                      <td className="px-3 py-2 text-gold">{signed(w.mmNet)}</td>
                      <td className="px-3 py-2">{signed(w.commNet)}</td>
                      <td className="px-3 py-2">{signed(w.retailNet)}</td>
                      <td className="px-3 py-2">{fmt(w.openInterest)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        </>
      )}

      <section className="border-t border-border/60 bg-slate-deep/40">
        <div className="container mx-auto max-w-4xl px-4 py-14">
          <h2 className="font-display text-2xl font-bold">How to read the gold COT report</h2>
          <div className="mt-6 space-y-5">
            {FAQ.map((f) => (
              <div key={f.q}>
                <h3 className="font-semibold">{f.q}</h3>
                <p className="mt-1 text-sm text-muted-foreground">{f.a}</p>
              </div>
            ))}
            {cur && prv && (
              <div>
                <h3 className="font-semibold">Why do other sites sometimes show the opposite reading?</h3>
                <p className="mt-1 text-sm text-muted-foreground">
                  "Big funds" on this page means Managed Money (hedge funds and CTAs) in the CFTC Disaggregated report. Many
                  sites use the older Legacy report, whose "Non-Commercial" group combines Managed Money with Other
                  Reportables. This week Other Reportables changed their net position by{" "}
                  {cur.otherNet - prv.otherNet >= 0 ? "+" : "-"}{Math.abs(cur.otherNet - prv.otherNet).toLocaleString("en-US")}{" "}
                  contracts, so the combined Legacy-style figure moved by{" "}
                  {(() => { const v = ((cur.positions.managedMoney.long - cur.positions.managedMoney.short) - (prv.positions.managedMoney.long - prv.positions.managedMoney.short)) + (cur.otherNet - prv.otherNet); return `${v >= 0 ? "+" : "-"}${Math.abs(v).toLocaleString("en-US")}`; })()}.
                  Both readings can be correct at the same time because they measure different groups.
                </p>
              </div>
            )}
          </div>
          <div className="mt-10 rounded-xl border border-gold/30 bg-card/60 p-6">
            <h3 className="font-display text-xl font-bold">From positioning to execution</h3>
            <p className="mt-2 text-sm text-muted-foreground">
              Positioning sets the backdrop. Entries come from structure: liquidity sweep, displacement and a qualified
              POI. Scan your own chart with StructureLens for free, or review how the published setups have performed.
            </p>
            <div className="mt-4 flex flex-wrap gap-3">
              <Button asChild variant="gold">
                <Link to="/signals">Create your free account</Link>
              </Button>
              <Button asChild variant="outline">
                <Link to="/track-record">Review the Track Record</Link>
              </Button>
            </div>
            <div className="mt-4 flex flex-wrap gap-4 text-sm">
              <Link to="/gold-inflation-cpi" className="text-gold hover:underline">US CPI and Gold</Link>
              <Link to="/gold-analysis" className="text-gold hover:underline">Gold market analysis</Link>
              <Link to="/education" className="text-gold hover:underline">Education hub</Link>
            </div>
          </div>
          <p className="mt-6 text-xs text-muted-foreground">
            Data: U.S. Commodity Futures Trading Commission, Disaggregated Futures Only report, COMEX Gold (088691). For
            educational and research purposes only. Not investment advice.
          </p>
        </div>
      </section>
    </Layout>
  );
}

function Stat({ label, value, sub, tone }: { label: string; value: string; sub: string; tone?: string }) {
  return (
    <div className="rounded-xl border border-border/60 bg-card/60 p-5">
      <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">{label}</div>
      <div className="mt-2 font-mono text-2xl font-semibold">{value}</div>
      <div className={`mt-1 font-mono text-[11px] uppercase tracking-wider ${tone ?? "text-muted-foreground"}`}>{sub}</div>
    </div>
  );
}

function ReleaseStatus({ asOf }: { asOf: string }) {
  const [now, setNow] = useState<Date | null>(null);
  useEffect(() => { setNow(new Date()); const t = setInterval(() => setNow(new Date()), 60_000); return () => clearInterval(t); }, []);
  if (!now) return null;
  const next = nextRelease(now);
  return (
    <div className="mt-3 flex flex-wrap items-center gap-2">
      {isFreshReport(asOf, now) && (
        <span role="status" className="rounded-full border border-gold/40 bg-gold-muted px-3 py-1 font-mono text-[10px] uppercase tracking-[0.2em] text-gold">New report released</span>
      )}
      <span className="font-mono text-[11px] uppercase tracking-[0.16em] text-muted-foreground">
        Next release: {next.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric", timeZone: "UTC" })} {next.toISOString().slice(11, 16)} UTC
      </span>
    </div>
  );
}
